import React, { Component } from 'react'
import PropTypes from 'prop-types'
import settingsConfig from '@/modules/settings/config'
import SelectInput from '@/modules/base/components/inputs/select-input/select-input'
import lang from '@/lang'

import { toast } from '@/modules/base/components/toast/toastConfig'
import request from '@/modules/base/util/request'
import TextInput from '@/modules/base/components/inputs/text-input/text-input'
import MenuListWithCreate from '@/modules/base/components/inputs/select-input/menu-list-with-create'
import { DEFAULT_UNIT } from '@/modules/base/util/constants'

class ArticleUnitInput extends Component {
  constructor(props) {
    super(props)

    const { value } = props

    this.state = {
      value: value ? { name: value } : undefined,
      defaultUnit: '',
      units: [],
      unitSelectOptions: [],
      isManualEntry: false,
      manualValue: '',
    }

    this.getUnits = this.getUnits.bind(this)
    this.onUnitChange = this.onUnitChange.bind(this)
    this.onUnitCreated = this.onUnitCreated.bind(this)
    this.getUnitSelectProps = this.getUnitSelectProps.bind(this)
    this.mapUnitsToSelectOptions = this.mapUnitsToSelectOptions.bind(this)
    this.MenuList = props => <MenuListWithCreate {...props} onCreateClick={this.startManualEntry} />
    this.startManualEntry = this.startManualEntry.bind(this)
    this.cancelManualEntry = this.cancelManualEntry.bind(this)
    this.confirmManualEntry = this.confirmManualEntry.bind(this)
    this._isMounted = false
  }

  componentDidMount() {
    this._isMounted = true
    this.getUnits()
      .then(() => {
        const { unitSelectOptions, value } = this.state
        const { useFirstUnitIfEmpty = true } = this.props
        const preferredDefault = this.props.defaultUnit || DEFAULT_UNIT
        const defaultUnit = unitSelectOptions.find(u => u.name === preferredDefault) || unitSelectOptions[0] || ''
        const initialValue = value || (useFirstUnitIfEmpty ? defaultUnit : null)
        this._isMounted && this.setState({ defaultUnit, value: initialValue }, () => { this.onUnitChange(initialValue) })
      })
  }

  componentWillUnmount() {
    this._isMounted = false
  }

  mapUnitsToSelectOptions(units) {
    return units.map(unit => ({ name: unit }))
  }

  getUnitSelectOptions(search, articleUnits, resolve) {
    const unitSelectOptions = this.mapUnitsToSelectOptions(articleUnits)
    this._isMounted && this.setState({ units: articleUnits, unitSelectOptions }, () => {
      if (search) {
        return resolve(unitSelectOptions.filter(unit => unit.name.toLowerCase().includes(search.toLowerCase())))
      }
      return resolve(unitSelectOptions)
    })
  }

  getUnits(search) {
    const { articleUnits } = this.props
    if (articleUnits) {
      return new Promise((resolve, reject) => {
        this.getUnitSelectOptions(search, articleUnits, resolve)
      })
    } else {
      return new Promise((resolve, reject) => {
        request(settingsConfig.miscellaneousData, { auth: true, isConcurrent: true })
          .then((response) => {
            const { body: { data: { articleUnits: units } } } = response
            this.getUnitSelectOptions(search, units, resolve)
          })
          .catch(error => reject(error))
      })
    }

  }

  getUnitSelectProps() {
    const {
      value,
      useFormik,
      label,
      name,
      styles,
      menuWidth,
      readOnly,
      showPlaceholder = true,
    } = this.props
    const { unitSelectOptions } = this.state

    return {
      label,
      name,
      readOnly,
      allowCreate: true,
      notAsync: true,
      customSelectOptions: {
        placeholder: showPlaceholder ? lang.selectInput.createPlaceholder : '',
        valueKey: 'name',
        labelKey: 'name',
        isClearable: false,
        loadOptions: this.getUnits,
        defaultOptions: false,
        menuWidth,
      },
      options: unitSelectOptions,
      value,
      useFormik,
      handleChange: this.onUnitChange,
      onOptionCreated: this.onUnitCreated,
      styles,
      customComponents: { MenuList: this.MenuList },
      createLabel: `+ ${lang.generics.new}`,
      createOnBlur: false,
    }
  }

  onUnitChange(option) {
    const { onChange } = this.props
    this._isMounted && this.setState({ value: option }, () => { onChange && onChange(option ? option.name : '') })
  }

  onUnitCreated(option) {
    if (!option || (typeof option === 'string' && option.trim() === '')) {
      this.startManualEntry()
      return
    }
    const { units: actualUnits } = this.state
    const unit = String(option).trim()
    const newUnits = [...actualUnits, unit]

    request(settingsConfig.article, {
      auth: true,
      method: 'POST',
      data: { units: newUnits },
    })
      .then(({ body: { data: { units } } }) => {
        const unitSelectOptions = this.mapUnitsToSelectOptions(units)
        toast.showToast({ message: lang.article.unitInput.optionCreated(unit) })
        this.onUnitChange({ name: unit })
        this._isMounted && this.setState({ units, unitSelectOptions, isManualEntry: false, manualValue: '' })
      })
  }

  startManualEntry() {
    this._isMounted && this.setState({ isManualEntry: true, manualValue: '' })
  }

  cancelManualEntry() {
    this._isMounted && this.setState({ isManualEntry: false, manualValue: '' })
  }

  confirmManualEntry() {
    const { manualValue } = this.state
    const trimmed = (manualValue || '').trim()
    if (!trimmed) {
      this.cancelManualEntry()
      return
    }
    this.onUnitCreated(trimmed)
  }

  render() {
    const { dataQsId, name, label } = this.props
    const unitSelectProps = this.getUnitSelectProps()
    const { isManualEntry, manualValue } = this.state

    if (isManualEntry) {
      return (
        <TextInput
          name={`${name || 'unit'}-manual`}
          value={manualValue}
          label={label}
          autoFocus
          useFormik={false}
          dataQsId={dataQsId}
          onChange={(val) => this.setState({ manualValue: val })}
          onEnter={this.confirmManualEntry}
          onBlur={this.confirmManualEntry}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              this.cancelManualEntry()
            }
          }}
          customClass={null}
          className={null}
        />
      )
    }

    return (
      <SelectInput {...unitSelectProps} dataQsId={dataQsId} />
    )
  }
}

ArticleUnitInput.propTypes = {
  value: PropTypes.string,
  useFormik: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  dataQsId: PropTypes.string.isRequired,
  useFirstUnitIfEmpty: PropTypes.bool,
  showPlaceholder: PropTypes.bool,
}

export default ArticleUnitInput
